import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { MdClose } from "react-icons/md";
import { FaInfoCircle } from "react-icons/fa";
import { productDraftApiService } from "../../../service/admin/productDraftApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import "./BulkDimentionUpdate.css";

const REQUIRED_UPLOAD_HEADERS = ["sku", "weight", "length", "height", "width", "category id"];
/** Below this row count, send one API request with all rows (avoids 10× calls for tiny uploads). */
const BULK_BATCH_MIN_ROWS = 100;
/** When splitting large uploads, use this many chunks (only if rows >= BULK_BATCH_MIN_ROWS). */
const BULK_CHUNK_COUNT = 10;

const splitIntoNBatches = (rows, n) => {
  if (!rows.length) return [];
  const batches = Array.from({ length: n }, () => []);
  const base = Math.floor(rows.length / n);
  let rem = rows.length % n;
  let idx = 0;
  for (let b = 0; b < n; b += 1) {
    const size = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem -= 1;
    batches[b] = rows.slice(idx, idx + size);
    idx += size;
  }
  return batches.filter((b) => b.length > 0);
};

/** One batch for small files; up to BULK_CHUNK_COUNT batches when row count >= BULK_BATCH_MIN_ROWS. */
const buildBulkUpdateBatches = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  if (rows.length < BULK_BATCH_MIN_ROWS) return [rows];
  return splitIntoNBatches(rows, BULK_CHUNK_COUNT);
};


const normalizeHeaderKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const parseNumberOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(String(value).trim());
  return Number.isFinite(num) ? num : null;
};

const normalizeUrl = (url) => {
  if (!url) return "";
  try { return String(url).replace(/^http:\/\//, "https://"); } catch { return url; }
};

const getVariantStableKey = (variant, idx = 0) =>
  String(variant?.skuId || variant?.specId || variant?.sku || `idx-${idx}`);

const getVariantIdPayload = (variant) => {
  if (variant?.skuId != null && String(variant.skuId).trim()) return { skuId: variant.skuId };
  if (variant?.specId != null && String(variant.specId).trim()) return { specId: variant.specId };
  return null;
};

const toNullableNumber = (val) => {
  if (val === undefined || val === null || String(val).trim() === "") return null;
  const parsed = Number(String(val).trim());
  return Number.isFinite(parsed) ? parsed : NaN;
};

const getVariantAttributesLabel = (variant) => {
  const attrs = Array.isArray(variant?.skuAttributes) ? variant.skuAttributes : [];
  const parts = attrs
    .map((a) => {
      const name = a?.attributeNameTrans || a?.attributeName || "";
      const value = a?.valueTrans || a?.value || "";
      if (!name && !value) return "";
      return `${name}${name && value ? ": " : ""}${value}`.trim();
    })
    .filter(Boolean);
  return parts.length ? parts.join(" | ") : "-";
};

export function VariantDetailsModal({ open, rowData, onClose, onUpdated }) {
  const message = useMessage();
  const [variantEdits, setVariantEdits] = useState({});
  const [applyAllEdit, setApplyAllEdit] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [savingAll, setSavingAll] = useState(false);

  useEffect(() => {
    if (!open) {
      setVariantEdits({});
      setApplyAllEdit({});
      setSavingKey("");
      setSavingAll(false);
      return;
    }
    // Fresh modal state each time it opens/row changes
    setVariantEdits({});
    setApplyAllEdit({});
    setSavingKey("");
    setSavingAll(false);
  }, [open, rowData?._id]);

  if (!open || !rowData?._id) return null;

  const rowId = rowData._id;
  const variants = Array.isArray(rowData?.raw?.variations) ? rowData.raw.variations : [];
  const headerTitle = rowData?.title || rowData?.raw?.title || "-";
  const headerSku = rowData?.sku || rowData?.raw?.sku || rowData?.raw?.parentSku || "-";

  const handleVariantFieldChange = (variantKey, field, value) => {
    setVariantEdits((prev) => ({
      ...prev,
      [variantKey]: {
        ...(prev[variantKey] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveSingleVariant = async (variant, variantKey) => {
    const rowEdit = variantEdits?.[variantKey] || {};
    const update = {};
    ["weight", "length", "width", "height"].forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(rowEdit, field)) return;
      const parsed = toNullableNumber(rowEdit[field]);
      if (Number.isNaN(parsed)) return;
      if (parsed !== null) update[field] = parsed;
    });
    if (Object.keys(update).length === 0) {
      message.showToast("Enter at least one valid value", ToastTypes.WARNING);
      return;
    }
    const idPayload = getVariantIdPayload(variant);
    if (!idPayload) {
      message.showToast("Variant identifier missing", ToastTypes.ERROR);
      return;
    }
    const key = `${rowId}-${variantKey}`;
    setSavingKey(key);
    try {
      const res = await productDraftApiService.updateDraftVariantDimensions(rowId, {
        updates: [{ ...idPayload, update }],
      });
      if (res?.status?.code === 200) {
        message.showToast("Variant updated", ToastTypes.SUCCESS);
        if (typeof onUpdated === "function") onUpdated();
      } else {
        message.showToast(res?.error?.message || "Failed to update variant", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Failed to update variant", ToastTypes.ERROR);
    } finally {
      setSavingKey("");
    }
  };

  const handleApplyAll = async () => {
    const update = {};
    ["weight", "length", "width", "height"].forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(applyAllEdit, field)) return;
      const parsed = toNullableNumber(applyAllEdit[field]);
      if (Number.isNaN(parsed)) return;
      if (parsed !== null) update[field] = parsed;
    });
    if (Object.keys(update).length === 0) {
      message.showToast("Enter at least one valid value to apply", ToastTypes.WARNING);
      return;
    }
    const validVariants = variants.filter((v) => getVariantIdPayload(v));
    if (!validVariants.length) {
      message.showToast("No variants available for update", ToastTypes.WARNING);
      return;
    }
    setSavingAll(true);
    try {
      const res = await productDraftApiService.updateDraftVariantDimensions(rowId, {
        applyToAll: true,
        update,
      });
      if (res?.status?.code === 200) {
        message.showToast("Updated all variants", ToastTypes.SUCCESS);
        setApplyAllEdit({});
        setVariantEdits({});
        if (typeof onUpdated === "function") onUpdated();
        if (typeof onClose === "function") onClose();
      } else {
        message.showToast(res?.error?.message || "Failed to update variants", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Failed to apply for all variants", ToastTypes.ERROR);
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="vdu-overlay" onClick={onClose}>
      <div className="vdu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vdu-header">
          <div>
            <span>Variant Details</span>
            <div className="vdu-subtitle">Title: {headerTitle}</div>
            <div className="vdu-subtitle">SKU: {headerSku}</div>
          </div>
          <button type="button" className="vdu-close" onClick={onClose} title="Close">
            <MdClose />
          </button>
        </div>
        <div className="vdu-content">
          <div className="vdu-apply">
            <div className="vdu-apply-title">Apply to All</div>
            <input className="vdu-input" type="number" placeholder="Weight (kg)" value={applyAllEdit.weight ?? ""} onChange={(e) => setApplyAllEdit((p) => ({ ...p, weight: e.target.value }))} />
            <input className="vdu-input" type="number" placeholder="Length (cm)" value={applyAllEdit.length ?? ""} onChange={(e) => setApplyAllEdit((p) => ({ ...p, length: e.target.value }))} />
            <input className="vdu-input" type="number" placeholder="Width (cm)" value={applyAllEdit.width ?? ""} onChange={(e) => setApplyAllEdit((p) => ({ ...p, width: e.target.value }))} />
            <input className="vdu-input" type="number" placeholder="Height (cm)" value={applyAllEdit.height ?? ""} onChange={(e) => setApplyAllEdit((p) => ({ ...p, height: e.target.value }))} />
            <button type="button" className="imp-btn imp-btn-primary" disabled={savingAll} onClick={handleApplyAll}>
              {savingAll ? "Applying..." : "Apply to All"}
            </button>
          </div>

          <div className="imp-table-wrap">
            <table className="imp-table vdu-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Image</th>
                  <th>Attributes</th>
                  <th>Weight (kg)</th>
                  <th>Length (cm)</th>
                  <th>Width (cm)</th>
                  <th>Height (cm)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {variants.length > 0 ? variants.map((v, idx) => {
                  const vKey = getVariantStableKey(v, idx);
                  const edit = variantEdits[vKey] || {};
                  const skuAttrImage = (Array.isArray(v?.skuAttributes) ? v.skuAttributes : [])
                    .map((a) => a?.skuImageUrl)
                    .find((u) => typeof u === "string" && u.trim());
                  const image = normalizeUrl(v?.thumbnailImage || skuAttrImage || "");
                  return (
                    <tr key={`${vKey}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{image ? <img src={image} alt="" className="vdu-img" /> : "-"}</td>
                      <td title={getVariantAttributesLabel(v)}>{getVariantAttributesLabel(v)}</td>
                      <td><input className="vdu-input" type="number" value={edit.weight ?? v?.weight ?? ""} onChange={(e) => handleVariantFieldChange(vKey, "weight", e.target.value)} /></td>
                      <td><input className="vdu-input" type="number" value={edit.length ?? v?.length ?? ""} onChange={(e) => handleVariantFieldChange(vKey, "length", e.target.value)} /></td>
                      <td><input className="vdu-input" type="number" value={edit.width ?? v?.width ?? ""} onChange={(e) => handleVariantFieldChange(vKey, "width", e.target.value)} /></td>
                      <td><input className="vdu-input" type="number" value={edit.height ?? v?.height ?? ""} onChange={(e) => handleVariantFieldChange(vKey, "height", e.target.value)} /></td>
                      <td>
                        <button
                          type="button"
                          className="imp-btn imp-btn-primary imp-btn-sm vdu-save-btn"
                          disabled={savingKey === `${rowId}-${vKey}`}
                          onClick={() => handleSaveSingleVariant(v, vKey)}
                        >
                          {savingKey === `${rowId}-${vKey}` ? "Saving..." : "Save"}
                        </button>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr className="imp-no-data"><td colSpan={8}>No variant data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const BulkDimensionUpdate = forwardRef(function BulkDimensionUpdate({ onUpdateComplete }, ref) {
  const [uploadRows, setUploadRows] = useState([]);
  const [uploadFileName, setUploadFileName] = useState("");
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateSummary, setBulkUpdateSummary] = useState(null);
  const [showBulkSummaryModal, setShowBulkSummaryModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [bulkProgressPercent, setBulkProgressPercent] = useState(0);
  const fileInputRef = useRef(null);
  const message = useMessage();

  const clearUploadSelection = () => {
    setUploadRows([]);
    setUploadFileName("");
    setBulkUpdateSummary(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useImperativeHandle(ref, () => ({ clearUpload: clearUploadSelection }), []);

  const parseExcelFile = async (file) => {
    const workbook = new ExcelJS.Workbook();
    const buffer = await file.arrayBuffer();
    await workbook.xlsx.load(buffer);
    const worksheet = workbook.worksheets?.[0];
    if (!worksheet) throw new Error("Excel sheet not found");

    const headerRow = worksheet.getRow(1);
    const headerMap = {};
    headerRow.eachCell((cell, colNumber) => {
      const key = normalizeHeaderKey(cell?.value);
      if (key) headerMap[key] = colNumber;
    });

    const missingHeaders = REQUIRED_UPLOAD_HEADERS.filter((h) => !headerMap[h]);
    if (missingHeaders.length) {
      throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
    }

    const parsedRows = [];
    for (let rowNum = 2; rowNum <= worksheet.rowCount; rowNum += 1) {
      const row = worksheet.getRow(rowNum);
      const sku = String(row.getCell(headerMap["sku"]).value || "").trim();
      if (!sku) continue;

      parsedRows.push({
        sku,
        weight: parseNumberOrNull(row.getCell(headerMap["weight"]).value),
        length: parseNumberOrNull(row.getCell(headerMap["length"]).value),
        height: parseNumberOrNull(row.getCell(headerMap["height"]).value),
        width: parseNumberOrNull(row.getCell(headerMap["width"]).value),
        categoryId: String(row.getCell(headerMap["category id"]).value || "").trim(), // optional
      });
    }
    return parsedRows;
  };

  const handleExcelFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await parseExcelFile(file);
      if (!parsed.length) {
        setUploadRows([]);
        setUploadFileName("");
        setBulkUpdateSummary(null);
        message.showToast("No valid SKU rows found in the Excel file", ToastTypes.WARNING);
        return;
      }
      setUploadRows(parsed);
      setUploadFileName(file.name);
      setBulkUpdateSummary(null);
      message.showToast(`${parsed.length} row(s) ready for update`, ToastTypes.SUCCESS);
    } catch (error) {
      setUploadRows([]);
      setUploadFileName("");
      setBulkUpdateSummary(null);
      message.showToast(error?.message || "Failed to read Excel file", ToastTypes.ERROR);
    } finally {
      e.target.value = "";
    }
  };

  const handleBulkUpdateFromExcel = async () => {
    if (!uploadRows.length) {
      message.showToast("Please upload a valid Excel file first", ToastTypes.WARNING);
      return;
    }

    setShowBulkSummaryModal(true);
    setIsSummaryLoading(true);
    setBulkUpdateSummary(null);
    setBulkProgressPercent(0);
    setIsBulkUpdating(true);
    const merged = { updated: [], skipped: [], failed: [] };
    const batches = buildBulkUpdateBatches(uploadRows);
    try {
      for (let i = 0; i < batches.length; i += 1) {
        const res = await productDraftApiService.bulkUpdateDraftDimensions(batches[i], {
          batchIndex: i + 1,
          batchTotal: batches.length,
        });
        const pct =
          typeof res?.data?.progressPercent === "number"
            ? res.data.progressPercent
            : Math.min(100, Math.round(((i + 1) / batches.length) * 100));
        setBulkProgressPercent(pct);
        if (res?.status?.code !== 200) {
          message.showToast(res?.error?.message || "Bulk update failed", ToastTypes.ERROR);
          setBulkUpdateSummary({
            ...merged,
            failed: [
              ...merged.failed,
              { sku: "-", reason: res?.error?.message || "Bulk update failed" },
            ],
          });
          return;
        }
        const apiSummary = res?.data || {};
        merged.updated.push(...(apiSummary.updated || []));
        merged.skipped.push(...(apiSummary.skipped || []));
        merged.failed.push(...(apiSummary.failed || []));
      }
      setBulkUpdateSummary({
        updated: merged.updated,
        skipped: merged.skipped,
        failed: merged.failed,
      });
      message.showToast("Update completed successfully", ToastTypes.SUCCESS);
      if (typeof onUpdateComplete === "function") onUpdateComplete();
    } catch (error) {
      message.showToast(error?.message || "Bulk update failed", ToastTypes.ERROR);
      setBulkUpdateSummary({
        ...merged,
        failed: [
          ...merged.failed,
          { sku: "-", reason: error?.message || "Bulk update failed" },
        ],
      });
    } finally {
      setIsBulkUpdating(false);
      setIsSummaryLoading(false);
    }
  };

  const exportSkuListToExcel = async (rows, fileName) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      message.showToast("No SKU data available to export", ToastTypes.WARNING);
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("SKUs");
    worksheet.columns = [{ header: "SKU", key: "sku", width: 28 }];

    rows.forEach((item) => {
      const skuValue = typeof item === "string" ? item : item?.sku;
      worksheet.addRow({ sku: String(skuValue || "").trim() });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("SKU Upload Template");
      worksheet.columns = [
        { header: "SKU", key: "sku", width: 24 },
        { header: "Weight (kg)", key: "weight", width: 14 },
        { header: "Length (cm)", key: "length", width: 14 },
        { header: "Height (cm)", key: "height", width: 14 },
        { header: "Width (cm)", key: "width", width: 14 },
        { header: "Category ID", key: "categoryId", width: 18 },
      ];
      // no need to sample data as it is not required
      // worksheet.addRow({
      //   sku: "sample-sku-001",
      //   weight: 0,
      //   length: 0,
      //   height: 0,
      //   width: 0,
      //   categoryId: "",
      // });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sku_upload_template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.showToast("Failed to download template", ToastTypes.ERROR);
    }
  };

  const hasUploadReady = uploadRows.length > 0;

  return (
    <>
      <div className="bdu-top-row">
        <div className="bdu-top-row-left">
          <button
            type="button"
            className="bdu-info-btn"
            onClick={() => setShowInfoModal(true)}
            title="How to use template and upload"
            aria-label="How to use template and upload"
          >
            <FaInfoCircle />
          </button>
          <button
            type="button"
            className="pd-toolbar-btn bdu-btn"
            onClick={handleDownloadTemplate}
            disabled={isBulkUpdating}
            title="Download SKU, Weight & Dimension Excel template"
          >
            Download Template
          </button>
          <button
            type="button"
            className="pd-toolbar-btn bdu-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isBulkUpdating}
            title="Upload SKU, Weight & Dimension Excel file"
          >
            Upload Dimension
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="bdu-hidden-input"
            onChange={handleExcelFileChange}
          />
          {uploadFileName ? (
            <span
              className={`pd-upload-file-name bdu-file-name${isBulkUpdating ? " bdu-file-name--disabled" : ""}`}
              role="button"
              tabIndex={isBulkUpdating ? -1 : 0}
              title="Choose a different Excel file"
              onClick={() => !isBulkUpdating && fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (isBulkUpdating) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              {uploadFileName}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="pd-toolbar-btn bdu-btn pd-btn-success"
          onClick={handleBulkUpdateFromExcel}
          disabled={isBulkUpdating || !hasUploadReady}
          title="Apply bulk update from the loaded Excel file"
        >
          {isBulkUpdating ? "Updating..." : "Upload"}
        </button>
      </div>

      {showBulkSummaryModal && (
        <div className="bdu-overlay">
          <div className="bdu-modal">
            <div className="bdu-header">
              <span>Bulk Dimension Update Result</span>
              <button
                type="button"
                onClick={() => {
                  setShowBulkSummaryModal(false);
                  setBulkProgressPercent(0);
                }}
                className="bdu-close"
                title="Close"
              >
                <MdClose />
              </button>
            </div>

            <div className="bdu-content">
              {isSummaryLoading && (
                <div className="bdu-loading-wrap">
                  <div className="bdu-loading-spinner" />
                  <div>Updating SKUs, please wait...</div>
                  <div className="bdu-progress-wrap">
                    <div className="bdu-progress-bar" aria-hidden>
                      <div
                        className="bdu-progress-fill"
                        style={{ width: `${bulkProgressPercent}%` }}
                      />
                    </div>
                    <div className="bdu-progress-label">{bulkProgressPercent}% complete</div>
                  </div>
                </div>
              )}
              {!isSummaryLoading && bulkUpdateSummary && (
                <>
              <div className="bdu-counts">
                <div className="bdu-count bdu-count--updated">
                  Updated: {bulkUpdateSummary.updated?.length || 0}
                </div>
                <div className="bdu-count bdu-count--skipped">
                  Skipped: {bulkUpdateSummary.skipped?.length || 0}
                </div>
                <div className="bdu-count bdu-count--failed">
                  Failed: {bulkUpdateSummary.failed?.length || 0}
                </div>
              </div>

              <div className="bdu-cards">
                <div className="bdu-card bdu-card--updated">
                  <div className="bdu-card-title bdu-card-title--updated">
                    <span>Successfully Updated SKUs</span>
                    <button
                      type="button"
                      className="bdu-export-btn bdu-export-btn--updated"
                      onClick={() => exportSkuListToExcel(bulkUpdateSummary.updated || [], "updated_skus.xlsx")}
                      disabled={!bulkUpdateSummary.updated?.length}
                    >
                      Export
                    </button>
                  </div>
                  <div className="bdu-list">
                    <div className="bdu-success-head">
                      <span>SKU</span>
                      <span>Weight (kg)</span>
                      <span>Length (cm)</span>
                      <span>Width (cm)</span>
                      <span>Height (cm)</span>
                      <span>Category</span>
                    </div>
                    {(bulkUpdateSummary.updated || []).map((row, idx) => {
                      const sku = typeof row === "string" ? row : row?.sku;
                      const weight = typeof row === "string" ? "-" : row?.weight;
                      const length = typeof row === "string" ? "-" : row?.length;
                      const width = typeof row === "string" ? "-" : row?.width;
                      const height = typeof row === "string" ? "-" : row?.height;
                      const categoryId = typeof row === "string" ? "-" : (row?.categoryId || "-");
                      const categoryPath = typeof row === "string" ? "" : (row?.categoryPath || "");
                      return (
                        <div key={`${sku || "u"}-${idx}`} className="bdu-success-row">
                          <span>{sku || "-"}</span>
                          <span>{weight ?? "-"}</span>
                          <span>{length ?? "-"}</span>
                          <span>{width ?? "-"}</span>
                          <span>{height ?? "-"}</span>
                          <span title={categoryPath}>{categoryId}</span>
                        </div>
                      );
                    })}
                    {!bulkUpdateSummary.updated?.length && <div className="bdu-empty">No updated SKU</div>}
                  </div>
                </div>

                <div className="bdu-card bdu-card--skipped">
                  <div className="bdu-card-title bdu-card-title--skipped">
                    <span>Skipped SKUs</span>
                    <button
                      type="button"
                      className="bdu-export-btn bdu-export-btn--skipped"
                      onClick={() =>
                        exportSkuListToExcel(
                          (bulkUpdateSummary.skipped || []).map((item) => item?.sku).filter(Boolean),
                          "skipped_skus.xlsx"
                        )
                      }
                      disabled={!bulkUpdateSummary.skipped?.length}
                    >
                      Export
                    </button>
                  </div>
                  <div className="bdu-list">
                    {(bulkUpdateSummary.skipped || []).map((item, idx) => (
                      <div key={`${item?.sku || "skip"}-${idx}`} className="bdu-row">
                        <strong>{item?.sku || "-"}</strong> - {item?.reason || "This SKU does not exist in the database."}
                      </div>
                    ))}
                    {!bulkUpdateSummary.skipped?.length && <div className="bdu-empty">No skipped SKU</div>}
                  </div>
                </div>

                {(bulkUpdateSummary.failed || []).length > 0 && (
                  <div className="bdu-card bdu-card--failed">
                    <div className="bdu-card-title bdu-card-title--failed">
                      <span>Failed Updates (reason)</span>
                      <button
                        type="button"
                        className="bdu-export-btn bdu-export-btn--failed"
                        onClick={() =>
                          exportSkuListToExcel(
                            (bulkUpdateSummary.failed || []).map((item) => item?.sku).filter(Boolean),
                            "failed_skus.xlsx"
                          )
                        }
                        disabled={!bulkUpdateSummary.failed?.length}
                      >
                        Export
                      </button>
                    </div>
                    <div className="bdu-list">
                      {(bulkUpdateSummary.failed || []).map((item, idx) => (
                        <div key={`${item?.sku || "fail"}-${idx}`} className="bdu-row">
                          <strong>{item?.sku || "-"}</strong> - {item?.reason || "Update failed"}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showInfoModal && (
        <div className="bdu-overlay">
          <div className="bdu-modal bdu-info-modal">
            <div className="bdu-header">
              <span>How Bulk Dimension Update Works</span>
              <button
                type="button"
                onClick={() => setShowInfoModal(false)}
                className="bdu-close"
                title="Close"
              >
                <MdClose />
              </button>
            </div>
            <div className="bdu-content bdu-info-content">
              <div className="bdu-info-grid">
                <section className="bdu-info-section">
                  <h4>Download Template</h4>
                  <ul className="bdu-info-list">
                    <li>Downloads the standard Excel format for bulk dimension update.</li>
                    <li>Use this file only; do not rename/remove headers.</li>
                    <li>Template columns: <strong>SKU, Weight (kg), Length (cm), Height (cm), Width (cm), Category ID (optional)</strong>.</li>
                  </ul>
                </section>

                <section className="bdu-info-section">
                  <h4>Upload Dimension</h4>
                  <ul className="bdu-info-list">
                    <li>Upload an Excel file with SKU, Weight, Length, Height, Width, and Category ID(optional) columns.</li>
                  </ul>
                </section>

                <section className="bdu-info-section">
                  <h4>Upload</h4>
                  <ul className="bdu-info-list">
                    <li>Click the Upload button to start the bulk update process.</li>
                    <li>The result will show <strong>Updated</strong>, <strong>Skipped</strong>, and <strong>Failed</strong> SKUs.</li>
                  </ul>
                </section>

                <section className="bdu-info-section">
                  <h4>All Draft Products (Error Filter)</h4>
                  <ul className="bdu-info-list">
                    <li>Choose which draft set to view: all drafts, with errors, or without errors.</li>
                    <li>Changes the table result and other filter counts.</li>
                  </ul>
                </section>

                <section className="bdu-info-section">
                  <h4>All global categories</h4>
                  <ul className="bdu-info-list">
                    <li>Filter drafts by one or multiple global categories.</li>
                    <li>Use search inside dropdown to find by category name or ID.</li>
                    <li>Use <strong>Show more</strong> to load additional categories.</li>
                  </ul>
                </section>

                <section className="bdu-info-section">
                  <h4>Export SKUs</h4>
                  <ul className="bdu-info-list">
                    <li>Exports a list of SKUs in Excel file for currently applied filters.</li>
                    <li>Main use: prepare filtered SKUs for bulk dimension update.</li>
                  </ul>
                </section>

                <section className="bdu-info-section bdu-info-section--important bdu-info-section--wide">
                  <h4>Important Instructions</h4>
                  <ul className="bdu-info-list">
                    <li>Do not keep SKU blank; blank rows are ignored.</li>
                    <li>Keep dimensions numeric and positive values only.</li>
                    <li>If SKU does not exist, it appears in skipped/failed with reason.</li>
                  </ul>
                </section>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default BulkDimensionUpdate;
